// Share utility functions for social media platforms

export interface ShareData {
  title: string;
  url: string;
  description?: string;
  hashtags?: string[];
}

/**
 * Share to Facebook
 */
export const shareToFacebook = (data: ShareData) => {
  const { title, url } = data;
  const encodedUrl = encodeURIComponent(url);
  const encodedTitle = encodeURIComponent(title);
  
  const facebookUrl = `https://www.facebook.com/sharer/sharer.php?u=${encodedUrl}&quote=${encodedTitle}`;
  window.open(facebookUrl, '_blank', 'width=600,height=400');
};

/**
 * Share to Twitter
 */
export const shareToTwitter = (data: ShareData) => {
  const { title, url, hashtags } = data;
  const encodedUrl = encodeURIComponent(url);
  const encodedTitle = encodeURIComponent(title);
  const hashtagsString = hashtags ? hashtags.map(tag => `#${tag}`).join(' ') : '';
  const encodedHashtags = encodeURIComponent(hashtagsString);
  
  const twitterUrl = `https://twitter.com/intent/tweet?text=${encodedTitle}&url=${encodedUrl}&hashtags=${encodedHashtags}`;
  window.open(twitterUrl, '_blank', 'width=600,height=400');
};

/**
 * Share to LinkedIn
 */
export const shareToLinkedIn = (data: ShareData) => {
  const { title, url, description } = data;
  const encodedUrl = encodeURIComponent(url);
  const encodedTitle = encodeURIComponent(title);
  const encodedDescription = description ? encodeURIComponent(description) : '';
  
  const linkedinUrl = `https://www.linkedin.com/sharing/share-offsite/?url=${encodedUrl}&title=${encodedTitle}&summary=${encodedDescription}`;
  window.open(linkedinUrl, '_blank', 'width=600,height=400');
};

/**
 * Copy link to clipboard
 */
export const copyToClipboard = async (url: string): Promise<boolean> => {
  try {
    await navigator.clipboard.writeText(url);
    return true;
  } catch (err) {
    // Fallback for older browsers
    const textArea = document.createElement('textarea');
    textArea.value = url;
    document.body.appendChild(textArea);
    textArea.focus();
    textArea.select();
    try {
      document.execCommand('copy');
      document.body.removeChild(textArea);
      return true;
    } catch (fallbackErr) {
      document.body.removeChild(textArea);
      return false;
    }
  }
};

/**
 * Share via WhatsApp
 */
export const shareToWhatsApp = (data: ShareData) => {
  const { title, url } = data;
  const encodedText = encodeURIComponent(`${title} ${url}`);
  const whatsappUrl = `https://wa.me/?text=${encodedText}`;
  window.open(whatsappUrl, '_blank');
};

/**
 * Share via Telegram
 */
export const shareToTelegram = (data: ShareData) => {
  const { title, url } = data;
  const encodedText = encodeURIComponent(`${title} ${url}`);
  const telegramUrl = `https://t.me/share/url?url=${encodeURIComponent(url)}&text=${encodedText}`;
  window.open(telegramUrl, '_blank');
};

/**
 * Get the current page URL
 */
export const getCurrentUrl = (): string => {
  if (typeof window !== 'undefined') {
    return window.location.href;
  }
  return '';
};

/**
 * Get the current page title
 */
export const getCurrentTitle = (): string => {
  if (typeof document !== 'undefined') {
    return document.title;
  }
  return '';
};
